import { useState } from 'react';
import PropTypes from 'prop-types';
import { breakpoints, Button, Heading, LinkButton, PageBlock, Stack, vars } from '@etg/wings';
import { DangerousHtmlContent, Icon, Notification, variants } from '@eti/components';
import {
  DIRECTIONS,
  useDirection,
  useProperty,
  useSnowplowTracking,
  useTranslation,
} from '@eti/providers';
import { css, getTextStyles } from '@eti/styles';
import { FareRule } from '@eti/types';
import { string as stringUtil, constants } from '@eti/utils';
import { useHistory } from 'react-router-dom';
import { useScrollToElementViaQueryParams } from '../../../common/custom-hooks/scrollToElementViaQueryParams';
import { routes } from '../../../constants/routesAndApisConstants';
import Sitejabber from '../../../survey/Sitejabber';
import { decorateUrlWithLangOverrideParam } from '../../../utils/ibeRequest';
import { scrollToElement } from '../../../utils/scrollToElement';
import { SHOW_FARE_RULES } from '../../order-details/constants/general';
import FareRulesOverlay from '../../payment/components/payment-conditions/overlays/FareRulesOverlay';
import { shouldShowFareRules } from '../utils/utils';
import MobileAppBanner from './ReceiptMobileAppBanner';
import SystemLcc from './ReceiptSystemLcc';

const { RTL } = DIRECTIONS;

const contentStyles = css`
  flex-wrap: wrap;

  @media (min-width: ${breakpoints._768}) {
    display: flex;
    justify-content: space-between;
  }
`;

const ScrollToPostBookingButtonStyles = css`
  font-size: 0.875rem;
  text-decoration: none;
`;

const messageTextWrapperStyles = css`
  margin-bottom: 1rem;

  @media (min-width: ${breakpoints._768}) {
    width: 65%;
  }
`;

const buttonLinksWrapperStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (min-width: ${breakpoints._768}) {
    width: 30%;
  }
`;

const informationClass = css`
  margin: 1.5rem 0 0;
`;

const travelDocumentIconClass = css`
  fill: ${vars.colors.button.primary.default.label};
  flex-shrink: 0;

  a:hover & {
    fill: ${vars.colors.button.primary.hover.label};
  }

  a:active & {
    fill: ${vars.colors.button.primary.active.label};
  }
`;

const ENGLISH_LANGUAGE_ID = 1;

const ReceiptConfirmationMessage = ({
  cancellationMessage,
  customerDetails,
  fareRules = [],
  hasRecentPostBookingOrder,
  languageCode,
  orderNumber,
  purchaseDate,
  shouldShowOrderInformation,
  texts,
  travelDocUrl,
}) => {
  const { p } = useProperty();
  const [isFareRulesModalOpen, setIsFareRulesModalOpen] = useState(false);
  useScrollToElementViaQueryParams('documents-button-section', SHOW_FARE_RULES, () =>
    setIsFareRulesModalOpen(true),
  );
  const { t } = useTranslation();
  const { direction } = useDirection();
  const {
    confirmationAndWelcomeContent,
    confirmationAndWelcomeTitle,
    systemLCCTitleAndContent,
    fareRulesDescription,
  } = texts;

  const { sendTrackEvent, isSnowplowTrackingEnabled } = useSnowplowTracking();

  const messageHeading = hasRecentPostBookingOrder
    ? t('Order.PostbookingPurchase.Completion.Header')
    : confirmationAndWelcomeTitle;

  const messageText = hasRecentPostBookingOrder
    ? t('Order.PostbookingPurchase.Completion.Text')
    : stringUtil.insertArgument(
        confirmationAndWelcomeContent,
        `<span class="${constants.MOUSE_FLOW_CLASS_NAMES}" data-testid="customer-email">${customerDetails?.email}</span>`,
      );

  const isDownloadAppEnabled = p('Order.DownloadApp.Enabled');

  const handleFareRulesClick = (value) => {
    return () => setIsFareRulesModalOpen(value);
  };

  const stringToHTML = (str) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(str, 'text/html');
    return doc.body;
  };

  /* systemLCCTitleAndContent provides dual info: 1. is it LCC?, 2. is there any LCC info to show? */
  /* if the value is '<h2></h2><div></div>', it means that the trip is LCC but there is no LCC info to show */
  /* if the value is '<h2>TEST</h2><div>TEST</div>', it means that the trip is LCC and there is LCC info to show */
  /* if the value is '' or null, it means that the trip is not LCC and there is no LCC info to show */
  /* `stringToHTML` has been used in order to decide if there is any LCC content to show */
  const isContentLCC = systemLCCTitleAndContent
    ? stringToHTML(systemLCCTitleAndContent).textContent
    : false;
  const showFareRules = shouldShowFareRules({ p, t, fareRules, texts });

  const history = useHistory();
  const handleShowBookings = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({ category: 'Confirmation Page', action: 'click', label: 'MyPortal button' });
    }
    history.push(routes.ORDER_DETAILS);
  };

  const shouldShowSitejabber = Boolean(p('Order.SitejabberWidget.Enabled') && orderNumber);

  return (
    <PageBlock withPaddingY>
      <Stack spacing={16}>
        {cancellationMessage && (
          <Notification title={cancellationMessage.header} variant={variants.INFORMATION}>
            {cancellationMessage.text}
          </Notification>
        )}
        <Heading level={1}>{messageHeading}</Heading>
        <div className={contentStyles}>
          <div className={messageTextWrapperStyles}>
            <DangerousHtmlContent className={getTextStyles()} content={messageText} />
            {hasRecentPostBookingOrder ? (
              <LinkButton
                className={ScrollToPostBookingButtonStyles}
                data-testid="orderPage-postBooking-orders-scrollToButton"
                onClick={() => scrollToElement('div[data-testid="orderPage-postBooking-orders"]')}
              >
                {t('Order.PostbookingPurchase.Completion.Link.Label')}
              </LinkButton>
            ) : null}
          </div>
          <div className={buttonLinksWrapperStyles}>
            <Button data-testid="myportal-button" onClick={handleShowBookings} variant="primary">
              {t('Order.MyPortal.Button.Text')}
            </Button>
            <Button
              as="a"
              data-testid="traveldoc-link"
              href={
                direction === RTL
                  ? decorateUrlWithLangOverrideParam(travelDocUrl, ENGLISH_LANGUAGE_ID)
                  : travelDocUrl
              }
              iconPrefix={<Icon className={travelDocumentIconClass} id="travel-document" />}
              rel="noreferrer"
              target="_blank"
              variant="primary"
            >
              {t('Receipt.ConfirmationMessage.TravelDocLinkLabel')}
            </Button>

            {showFareRules && (
              <>
                <Button
                  data-testid="fare-rules-link"
                  onClick={handleFareRulesClick(true)}
                  variant="primary"
                >
                  <span>{t('Order.ShowFareRules.Button.Text')}</span>
                </Button>
                <FareRulesOverlay
                  fareRules={fareRules}
                  fareRulesDescription={fareRulesDescription}
                  fareRulesHeaderText={t('Payment.FareRules.Modal.HeaderText')}
                  isOpen={isFareRulesModalOpen}
                  onClose={handleFareRulesClick(false)}
                />
              </>
            )}
          </div>
        </div>
        {shouldShowSitejabber && (
          <Sitejabber
            email={customerDetails?.email}
            language={languageCode}
            name={customerDetails?.name}
            orderNumber={orderNumber}
            phone={customerDetails?.phone}
            purchaseDate={purchaseDate}
          />
        )}
        {isDownloadAppEnabled && <MobileAppBanner />}
        {isContentLCC && (
          <SystemLcc
            contentLCC={systemLCCTitleAndContent}
            hideInfoLabel={t('Receipt.SystemMessage.Label.HideContent')}
            showInfoLabel={t('Receipt.SystemMessage.Label.ShowContent')}
          />
        )}
        {shouldShowOrderInformation && (
          <DangerousHtmlContent
            className={informationClass}
            content={t('IbeClient.OrderInformation.Text')}
            dataTestId="order-information"
          />
        )}
      </Stack>
    </PageBlock>
  );
};

ReceiptConfirmationMessage.propTypes = {
  cancellationMessage: PropTypes.shape({
    header: PropTypes.string,
    text: PropTypes.string,
  }),
  customerDetails: PropTypes.shape({
    city: PropTypes.string,
    email: PropTypes.string,
    name: PropTypes.string,
    nationality: PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    }),
    phone: PropTypes.string,
    street: PropTypes.string,
    zipCode: PropTypes.string,
  }),
  fareRules: PropTypes.arrayOf(FareRule),
  hasRecentPostBookingOrder: PropTypes.bool,
  languageCode: PropTypes.string,
  orderNumber: PropTypes.string,
  purchaseDate: PropTypes.string,
  shouldShowOrderInformation: PropTypes.bool,
  texts: PropTypes.shape({
    confirmationAndWelcomeContent: PropTypes.string,
    confirmationAndWelcomeTitle: PropTypes.string,
    fareRulesDescription: PropTypes.string,
    systemLCCTitleAndContent: PropTypes.string,
  }),
  travelDocUrl: PropTypes.string,
};

export default ReceiptConfirmationMessage;
